import { Grid } from "@mui/material";
import { useAtomValue } from "jotai";
import { get, omit } from "lodash-es";
import { useMemo } from "react";
import { redirect, useNavigate, useParams } from "react-router";
import { useBrandKit } from "../../modules/brand-kit/hooks/use-brand-kit";
import { useBrandTones } from "../../modules/brand-kit/hooks/use-brand-tones";
import { useResetBrandingImageType } from "../../modules/brand-kit/hooks/use-reset-branding-image-type";
import { BrandingBar } from "../../modules/branding-bar/branding-bar";
import { DiscoverCard } from "../../modules/discover/carousel/discover-card";
import { programSubheadingOverrides } from "../../modules/discover/carousel/discover-carousels";
import { useDiscoverLibraryLoader } from "../../modules/discover/hooks/use-libary-loader";
import { libraryAtom } from "../../modules/discover/library/store";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { PageHeader } from "../../modules/layout/components/page-header";
import { PageHeading } from "../../modules/layout/components/page-heading";
import { Breadcrumbs } from "../../modules/tabbed-programs/breadcrumbs";
import { toSlug } from "../../utils/create-slug";

import type { FC } from "react";

const DiscoverProgramPage: FC = () => {
  useDiscoverLibraryLoader();
  const { branding, loading } = useBrandKit();
  const { data: brandTones, loading: loadingBrandTone } = useBrandTones();
  const brandTone = useMemo(() => brandTones?.find((b) => b.is_default), [brandTones]);
  const navigate = useNavigate();
  const { program_slug } = useParams<{
    program_slug: string;
  }>();
  const library = useAtomValue(libraryAtom);

  useResetBrandingImageType();
  const program = library.programs.find(({ slug }) => slug === program_slug);

  if (program === undefined) {
    redirect("/404");
    return null;
  }

  return (
    <Page title={`Discover: ${program.name} | ChangeEngine`}>
      <PageHeader>
        <Breadcrumbs crumbs={[{ to: "/discover", title: "Discover" }, { title: program.name }]} />
        <PageHeading heading={program.name} data-testid={`program-title-${toSlug(program.name)}`} />
      </PageHeader>
      {/* HERE */}
      <BrandingBar visualStyleSelector />
      <PageContent>
        {!loading && !loadingBrandTone && (
          <Grid
            container
            rowSpacing={5}
            columnSpacing={3}
            columns={{ xs: 2, sm: 3, md: 3, lg: 4, xl: 5 }}
          >
            {program.templates?.map((template) => (
              <Grid key={template.slug} size={{ xs: 1 }} minWidth={250}>
                <DiscoverCard
                  template={{
                    ...template,
                    program: omit(program, ["templates"]),
                  }}
                  onClick={() => {
                    void navigate({
                      pathname: `/discover/${program.slug}/${template.slug}`,
                      ...(brandTone
                        ? {
                            search: `?${new URLSearchParams({
                              tone: brandTone?.id,
                            }).toString()}`,
                          }
                        : {}),
                    });
                  }}
                  subheadingGetter={get(programSubheadingOverrides, program.slug)}
                  brandKitSelections={branding}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </PageContent>
    </Page>
  );
};

export default DiscoverProgramPage;
